import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'
import { usePageHeaderActions } from '@context/PageHeaderContext'
import Modal from '@components/shared/Modal'
import DeadlineEngineLearningPanel from './components/DeadlineEngineLearningPanel'
import DeadlineEngineCalendarPanel from './components/DeadlineEngineCalendarPanel'

const CONFIG_CATEGORIES = [
  {
    id: 'action_deadline_rules',
    title: 'Regras de prazo por ação',
    description: 'Defina qual regra de prazo será aplicada para cada ação interpretada pelo motor.',
    columns: ['action_code', 'deadline_rule_code', 'area', 'regime', 'microsystem', 'document_nature', 'priority', 'active', 'requires_human_review', 'notes'],
    fields: [
      ['action_code', 'Código da ação', 'text'],
      ['deadline_rule_code', 'Código da regra de prazo', 'text'],
      ['area', 'Área', 'text'],
      ['regime', 'Regime', 'text'],
      ['microsystem', 'Microssistema', 'text'],
      ['document_nature', 'Natureza do documento', 'text'],
      ['priority', 'Prioridade', 'number'],
      ['requires_human_review', 'Exige revisão humana', 'boolean'],
      ['active', 'Ativo', 'boolean'],
      ['notes', 'Observações', 'textarea'],
    ],
    emptyPayload: { action_code: '', deadline_rule_code: '', area: '', regime: '', microsystem: '', document_nature: '', priority: 0, requires_human_review: false, active: true, notes: '' },
  },
  {
    id: 'action_phrase_patterns',
    title: 'Frases de identificação de ações',
    description: 'Cadastre frases que ajudam o motor a reconhecer ações dentro da publicação. Qualquer edição revoga a autoridade atual e exige nova promoção.',
    columns: ['action_code', 'phrase', 'pattern_type', 'weight', 'learning_state', 'confidence', 'trusted', 'active', 'context_area', 'context_regime', 'context_microsystem', 'document_nature'],
    columnLabels: {
      action_code: 'Ação', phrase: 'Frase', pattern_type: 'Tipo', weight: 'Peso', learning_state: 'Aprendizado',
      confidence: 'Confiança (%)', trusted: 'Confiável', active: 'Ativo', context_area: 'Área',
      context_regime: 'Regime', context_microsystem: 'Microssistema', document_nature: 'Natureza do documento',
    },
    fields: [
      ['action_code', 'Código da ação', 'text'],
      ['phrase', 'Frase', 'textarea'],
      ['pattern_type', 'Tipo do padrão', 'select', ['STRONG', 'MEDIUM', 'WEAK', 'NEGATIVE']],
      ['weight', 'Peso', 'number'],
      ['active', 'Ativo', 'boolean'],
      ['context_area', 'Área de contexto', 'text'],
      ['context_regime', 'Regime de contexto', 'text'],
      ['context_microsystem', 'Microssistema de contexto', 'text'],
      ['document_nature', 'Natureza do documento', 'text'],
    ],
    emptyPayload: { action_code: '', phrase: '', pattern_type: 'MEDIUM', weight: 1, active: true, context_area: '', context_regime: '', context_microsystem: '', document_nature: '' },
  },
  {
    id: 'canonical_actions',
    title: 'Ações canônicas',
    description: 'Gerencie o catálogo de ações normalizadas que o motor pode interpretar.',
    columns: ['code', 'name', 'description', 'category', 'active'],
    fields: [
      ['code', 'Código', 'text'], ['name', 'Nome', 'text'], ['description', 'Descrição', 'textarea'],
      ['category', 'Categoria', 'text'], ['active', 'Ativo', 'boolean'],
    ],
    emptyPayload: { code: '', name: '', description: '', category: '', active: true },
  },
  {
    id: 'legal_context_signals',
    title: 'Identificadores de contexto jurídico',
    description: 'Defina os termos que identificam área, regime, microssistema e natureza documental. Novos códigos podem ser cadastrados sem publicar o motor.',
    columns: ['dimension', 'code', 'term', 'weight', 'polarity', 'priority', 'active'],
    columnLabels: { dimension: 'Dimensão', code: 'Identificador', term: 'Termo encontrado', weight: 'Peso', polarity: 'Polaridade', priority: 'Prioridade', active: 'Ativo' },
    fields: [
      ['dimension', 'Dimensão', 'select', ['AREA', 'REGIME', 'MICROSYSTEM', 'DOCUMENT_NATURE']],
      ['code', 'Identificador', 'text'], ['term', 'Termo ou expressão', 'text'], ['weight', 'Peso', 'number'],
      ['polarity', 'Polaridade', 'select', ['POSITIVE', 'NEGATIVE']], ['priority', 'Prioridade', 'number'], ['active', 'Ativo', 'boolean'],
    ],
    emptyPayload: { dimension: 'AREA', code: '', term: '', weight: 1, polarity: 'POSITIVE', priority: 0, active: true },
  },
  {
    id: 'legal_non_business_days',
    title: 'Dias não úteis jurídicos',
    description: 'Consulte dias importados e inclua manualmente feriados, suspensões e exceções que afetam o cálculo de prazos.',
    columns: ['date', 'description', 'scope', 'court', 'state', 'city', 'origin', 'active'],
    columnLabels: { date: 'Data', description: 'Descrição', scope: 'Escopo', court: 'Tribunal', state: 'UF', city: 'Cidade', origin: 'Origem', active: 'Ativo' },
    fields: [
      ['date', 'Data', 'date'], ['description', 'Descrição', 'text'], ['scope', 'Escopo', 'text'],
      ['court', 'Tribunal', 'text'], ['state', 'UF', 'text'], ['city', 'Cidade', 'text'], ['active', 'Ativo', 'boolean'],
    ],
    emptyPayload: { date: '', description: '', scope: '', court: '', state: '', city: '', active: true },
  },
  {
    id: 'calendars',
    title: 'Calendários judiciais',
    description: 'Sincronize fontes oficiais, acompanhe execuções e revise eventos antes de afetarem os prazos.',
    kind: 'calendars',
  },
  {
    id: 'learning',
    title: 'Aprendizado',
    description: 'Curadoria dos candidatos gerados pelo feedback humano e pelas importações históricas.',
    kind: 'learning',
  },
]

const CALENDAR_SUBCATEGORY_IDS = ['legal_non_business_days', 'calendars']
const CALENDAR_SUBCATEGORIES = CONFIG_CATEGORIES.filter((category) => CALENDAR_SUBCATEGORY_IDS.includes(category.id))
const CALENDAR_MANAGEMENT_CATEGORY = {
  id: 'calendar_management',
  title: 'Calendário jurídico',
  description: 'Gerencie dias não úteis e as importações de calendários judiciais em um único espaço.',
  kind: 'calendar_management',
}
const MAIN_CONFIG_CATEGORIES = [
  ...CONFIG_CATEGORIES.filter((category) => !CALENDAR_SUBCATEGORY_IDS.includes(category.id)),
]

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toLocaleString('pt-BR')
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function formatColumnValue(column, value) {
  if (column === 'confidence' && typeof value === 'number') return `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
  return formatValue(value)
}

function normalizeFilterText(value) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
}

function getRowTitle(row) {
  return row.name || row.phrase || row.description || row.action_code || row.code || row.id || 'configuração'
}

function isReadOnlyNonBusinessDay(category, row) {
  if (category.id !== 'legal_non_business_days') return false
  return ['IMPORTED', 'SEED'].includes(String(row.origin || '').trim().toUpperCase())
}

function normalizePayload(category, form) {
  return Object.fromEntries(category.fields.map(([key, , type]) => {
    const value = form[key]
    if (type === 'number') return [key, value === '' || value === null || value === undefined ? null : Number(value)]
    if (type === 'boolean') return [key, Boolean(value)]
    return [key, value === '' ? null : value]
  }))
}

function normalizeLegalArea(value) {
  if (!value) return ''
  const normalized = String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase().replace(/[\s-]+/g, '_')
  return { CIVIL: 'CIVEL', JUIZADO_ESPECIAL: 'JUIZADO', JUIZADOS_ESPECIAIS: 'JUIZADO' }[normalized] || normalized
}

function ConfigurationModal({ open, category, initialValue, saving, onClose, onSave }) {
  const [form, setForm] = useState(initialValue || category.emptyPayload)

  useEffect(() => {
    setForm(initialValue || category.emptyPayload)
  }, [category, initialValue, open])

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  return (
    <Modal isOpen={open} onClose={onClose} title={`${initialValue?.id ? 'Editar' : 'Nova'} configuração`}>
      <div className="deadline-settings-modal deadline-settings-form-modal">
        <p>{category.description}</p>
        <div className="deadline-settings-form-grid">
          {category.fields.map(([key, label, type, options]) => (
            <label key={key} className={`rules-field ${type === 'textarea' ? 'rules-field-wide' : ''}`}>
              <span className="rules-field-label">{label}</span>
              {type === 'textarea' ? <textarea rows="3" value={form[key] ?? ''} onChange={(event) => updateField(key, event.target.value)} /> : null}
              {type === 'boolean' ? <select value={form[key] ? 'true' : 'false'} onChange={(event) => updateField(key, event.target.value === 'true')}><option value="true">Sim</option><option value="false">Não</option></select> : null}
              {type === 'select' ? <select value={form[key] ?? ''} onChange={(event) => updateField(key, event.target.value)}>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select> : null}
              {!['textarea', 'boolean', 'select'].includes(type) ? <input type={type} value={type === 'date' && typeof form[key] === 'string' ? form[key].slice(0, 10) : form[key] ?? ''} onChange={(event) => updateField(key, event.target.value)} /> : null}
            </label>
          ))}
        </div>
        <div className="deadline-settings-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => onSave(normalizePayload(category, form))} disabled={saving}><i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} /> {saving ? 'Salvando...' : 'Salvar configuração'}</button>
        </div>
      </div>
    </Modal>
  )
}

function PromotionModal({ promotion, saving, onClose, onConfirm, areaOptions, documentNatureOptions }) {
  const [contextArea, setContextArea] = useState('')
  const [documentNature, setDocumentNature] = useState('')

  useEffect(() => {
    setContextArea(normalizeLegalArea(promotion?.contextArea ?? promotion?.context_area ?? promotion?.ContextArea))
    setDocumentNature(promotion?.documentNature ?? promotion?.document_nature ?? promotion?.DocumentNature ?? '')
  }, [promotion])

  if (!promotion) return null
  const canPromote = contextArea && documentNature

  return (
    <Modal isOpen onClose={onClose} title="Promover padrão de frase" size="small">
      <div className="deadline-settings-modal">
        <p>Deseja promover <strong>{promotion.phrase}</strong> para uso normal?</p>
        <p>Revise o contexto antes da promoção. Área e natureza do documento são obrigatórias.</p>
        <div className="deadline-settings-form-grid">
          <label className="rules-field"><span className="rules-field-label">Área</span><input list="deadline-context-area-options" value={contextArea} onChange={(event) => setContextArea(event.target.value)} placeholder="Ex.: CIVEL" /><datalist id="deadline-context-area-options">{areaOptions.map((option) => <option key={option} value={option} />)}</datalist></label>
          <label className="rules-field"><span className="rules-field-label">Natureza do documento</span><input list="deadline-context-document-nature-options" value={documentNature} onChange={(event) => setDocumentNature(event.target.value)} placeholder="Ex.: INTIMACAO" /><datalist id="deadline-context-document-nature-options">{documentNatureOptions.map((option) => <option key={option} value={option} />)}</datalist></label>
        </div>
        <div className="deadline-settings-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm({ context_area: contextArea, document_nature: documentNature })} disabled={saving || !canPromote}><i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} /> {saving ? 'Promovendo...' : 'Confirmar promoção'}</button>
        </div>
      </div>
    </Modal>
  )
}

export default function DeadlineEngineSettingsPage({ calendarOnly = false }) {
  const { showError, showSuccess } = useNotification()
  const setPageHeaderActions = usePageHeaderActions()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTabId, setActiveTabId] = useState(() => {
    if (calendarOnly) return CALENDAR_MANAGEMENT_CATEGORY.id
    const queryTab = searchParams.get('tab')
    return MAIN_CONFIG_CATEGORIES.some((category) => category.id === queryTab) ? queryTab : MAIN_CONFIG_CATEGORIES[0].id
  })
  const [calendarSection, setCalendarSection] = useState(() => {
    const queryTab = searchParams.get('tab')
    const querySection = searchParams.get('calendarSection')
    return CALENDAR_SUBCATEGORY_IDS.includes(querySection)
      ? querySection
      : (CALENDAR_SUBCATEGORY_IDS.includes(queryTab) ? queryTab : 'legal_non_business_days')
  })
  const [configurations, setConfigurations] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingRow, setEditingRow] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [phrasePatternSource, setPhrasePatternSource] = useState('ALL')
  const [phrasePatternLearningState, setPhrasePatternLearningState] = useState('ALL')
  const [phrasePatternPage, setPhrasePatternPage] = useState(1)
  const [phrasePatternPageSize, setPhrasePatternPageSize] = useState(20)
  const [nonBusinessDayCourt, setNonBusinessDayCourt] = useState('ALL')
  const [nonBusinessDayYear, setNonBusinessDayYear] = useState('ALL')
  const [nonBusinessDayOrigin, setNonBusinessDayOrigin] = useState('ALL')
  const [nonBusinessDayStatus, setNonBusinessDayStatus] = useState('ALL')
  const [nonBusinessDaySearch, setNonBusinessDaySearch] = useState('')
  const [promotion, setPromotion] = useState(null)
  const [preparingPromotion, setPreparingPromotion] = useState(false)

  const activeCategory = calendarOnly
    ? CALENDAR_MANAGEMENT_CATEGORY
    : MAIN_CONFIG_CATEGORIES.find((category) => category.id === activeTabId) || MAIN_CONFIG_CATEGORIES[0]
  const isCalendarManagement = activeCategory.kind === 'calendar_management'
  const configurationCategory = isCalendarManagement
    ? CALENDAR_SUBCATEGORIES.find((category) => category.id === calendarSection) || CALENDAR_SUBCATEGORIES[0]
    : activeCategory
  const isLearningCategory = activeCategory.kind === 'learning'
  const isCalendarCategory = configurationCategory.kind === 'calendars'
  const isSpecialCategory = isLearningCategory || isCalendarCategory
  const isPhrasePatternsCategory = configurationCategory.id === 'action_phrase_patterns'
  const isNonBusinessDaysCategory = configurationCategory.id === 'legal_non_business_days'
  const activeRows = useMemo(() => (Array.isArray(configurations[configurationCategory.id]) ? configurations[configurationCategory.id] : []), [configurationCategory.id, configurations])
  const contextOptions = useMemo(() => {
    const signals = Array.isArray(configurations.legal_context_signals) ? configurations.legal_context_signals : []
    const codesFor = (dimension) => [...new Set(signals.filter((signal) => signal.active !== false && signal.dimension === dimension).map((signal) => signal.code).filter(Boolean))].sort()
    return { areas: codesFor('AREA'), documentNatures: codesFor('DOCUMENT_NATURE') }
  }, [configurations.legal_context_signals])
  const nonBusinessDayFilterOptions = useMemo(() => {
    if (!isNonBusinessDaysCategory) return { courts: [], years: [], origins: [] }
    const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right, 'pt-BR', { numeric: true }))
    return {
      courts: uniqueSorted(activeRows.map((row) => row.court)),
      years: uniqueSorted(activeRows.map((row) => String(row.date ?? '').match(/^\d{4}/)?.[0])).reverse(),
      origins: uniqueSorted(activeRows.map((row) => row.origin)),
    }
  }, [activeRows, isNonBusinessDaysCategory])
  const filteredRows = useMemo(() => {
    if (isPhrasePatternsCategory) {
      return activeRows.filter((row) => (
        (phrasePatternSource === 'ALL' || row.source === phrasePatternSource)
        && (phrasePatternLearningState === 'ALL' || row.learning_state === phrasePatternLearningState)
      ))
    }
    if (isNonBusinessDaysCategory) {
      const normalizedSearch = normalizeFilterText(nonBusinessDaySearch.trim())
      return activeRows.filter((row) => {
        const courtMatches = nonBusinessDayCourt === 'ALL'
          || (nonBusinessDayCourt === 'NO_COURT' ? !row.court : row.court === nonBusinessDayCourt)
        const yearMatches = nonBusinessDayYear === 'ALL' || String(row.date ?? '').startsWith(nonBusinessDayYear)
        const originMatches = nonBusinessDayOrigin === 'ALL' || row.origin === nonBusinessDayOrigin
        const statusMatches = nonBusinessDayStatus === 'ALL'
          || (nonBusinessDayStatus === 'ACTIVE' ? row.active !== false : row.active === false)
        const searchMatches = !normalizedSearch || normalizeFilterText([
          row.description,
          row.scope,
          row.court,
          row.state,
          row.city,
          row.origin,
        ].filter(Boolean).join(' ')).includes(normalizedSearch)
        return courtMatches && yearMatches && originMatches && statusMatches && searchMatches
      })
    }
    return activeRows
  }, [activeRows, isNonBusinessDaysCategory, isPhrasePatternsCategory, nonBusinessDayCourt, nonBusinessDayOrigin, nonBusinessDaySearch, nonBusinessDayStatus, nonBusinessDayYear, phrasePatternLearningState, phrasePatternSource])
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / phrasePatternPageSize))
  const displayedRows = isPhrasePatternsCategory ? filteredRows.slice((phrasePatternPage - 1) * phrasePatternPageSize, phrasePatternPage * phrasePatternPageSize) : filteredRows

  const loadConfigurations = useCallback(async () => {
    setLoading(true)
    const response = await ApiService.getDeadlineEngineConfigurations()
    setLoading(false)
    if (!response.success) {
      showError(response.error || 'Não foi possível carregar as configurações atuais do motor.')
      return
    }
    setConfigurations(response.data || {})
  }, [showError])

  useEffect(() => {
    const actionLabel = calendarOnly ? 'Atualizar calendário' : 'Atualizar cadastros'
    setPageHeaderActions(<button type="button" className="btn btn-outline" onClick={loadConfigurations} disabled={loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} /> {loading ? 'Atualizando...' : actionLabel}</button>)
    return () => setPageHeaderActions(null)
  }, [calendarOnly, loadConfigurations, loading, setPageHeaderActions])

  useEffect(() => { loadConfigurations() }, [loadConfigurations])
  useEffect(() => {
    const tab = searchParams.get('tab')
    const section = searchParams.get('calendarSection')

    if (calendarOnly) {
      const requestedSection = CALENDAR_SUBCATEGORY_IDS.includes(section)
        ? section
        : (CALENDAR_SUBCATEGORY_IDS.includes(tab) ? tab : null)
      if (requestedSection && requestedSection !== calendarSection) setCalendarSection(requestedSection)
      return
    }

    if (tab === 'calendar_management' || CALENDAR_SUBCATEGORY_IDS.includes(tab)) {
      const legacySection = CALENDAR_SUBCATEGORY_IDS.includes(section)
        ? section
        : (CALENDAR_SUBCATEGORY_IDS.includes(tab) ? tab : 'legal_non_business_days')
      navigate(`/calendario-juridico?calendarSection=${legacySection}`, { replace: true })
      return
    }

    if (tab && MAIN_CONFIG_CATEGORIES.some((category) => category.id === tab) && tab !== activeTabId) setActiveTabId(tab)
  }, [activeTabId, calendarOnly, calendarSection, navigate, searchParams])
  useEffect(() => {
    setModalOpen(false)
    setEditingRow(null)
    setPhrasePatternPage(1)
  }, [activeTabId])
  useEffect(() => { setPhrasePatternPage((page) => Math.min(page, totalPages)) }, [totalPages])

  const selectTab = (tabId) => {
    setActiveTabId(tabId)
    const next = new URLSearchParams(searchParams)
    next.set('tab', tabId)
    next.delete('calendarSection')
    setSearchParams(next, { replace: true })
  }

  const selectCalendarSection = (sectionId) => {
    setCalendarSection(sectionId)
    const next = new URLSearchParams(searchParams)
    next.delete('tab')
    next.set('calendarSection', sectionId)
    setSearchParams(next, { replace: true })
  }

  const saveConfiguration = async (payload) => {
    setSaving(true)
    const response = editingRow?.id
      ? await ApiService.updateDeadlineEngineConfiguration(configurationCategory.id, editingRow.id, { ...payload, expected_version: editingRow.config_version })
      : await ApiService.createDeadlineEngineConfiguration(configurationCategory.id, payload)
    setSaving(false)
    if (!response.success) {
      showError(response.error || 'Não foi possível salvar a configuração.')
      return
    }
    showSuccess('Configuração salva com sucesso.')
    setModalOpen(false)
    await loadConfigurations()
  }

  const deleteConfiguration = async (row) => {
    if (!window.confirm(`Deseja realmente desativar ${getRowTitle(row)}?`)) return
    const response = await ApiService.deleteDeadlineEngineConfiguration(configurationCategory.id, row.id, row.config_version)
    if (!response.success) {
      showError(response.error || 'Não foi possível desativar a configuração.')
      return
    }
    showSuccess('Configuração desativada com sucesso.')
    await loadConfigurations()
  }

  const changeLearningState = async (row, learningState) => {
    if (learningState === 'GRADUATED') {
      setPreparingPromotion(true)
      const response = await ApiService.prepareDeadlinePhrasePatternPromotion(row.id)
      setPreparingPromotion(false)
      if (!response.success) {
        showError(response.error || 'Não foi possível preparar a promoção do padrão.')
        return
      }
      setPromotion({ id: row.id, ...response.data })
      return
    }
    if (!window.confirm(`Deseja alterar ${getRowTitle(row)} para ${learningState}?`)) return
    setSaving(true)
    const response = await ApiService.updateDeadlineEngineConfiguration('action_phrase_patterns', row.id, { learning_state: learningState, expected_version: row.config_version })
    setSaving(false)
    if (!response.success) {
      showError(response.error || 'Não foi possível alterar o estado do aprendizado.')
      return
    }
    showSuccess(`Padrão atualizado para ${learningState}.`)
    await loadConfigurations()
  }

  const confirmPromotion = async (context) => {
    setSaving(true)
    const response = await ApiService.promoteDeadlinePhrasePattern(promotion.id, { ...context, expected_version: promotion.configVersion })
    setSaving(false)
    if (!response.success) {
      showError(response.error || 'Não foi possível promover o padrão.')
      return
    }
    setPromotion(null)
    showSuccess('Padrão promovido com sucesso.')
    await loadConfigurations()
  }

  return (
    <div className="main-content rules-config-page deadline-engine-settings-page">
      {!calendarOnly ? (
        <div className="rules-tabs deadline-settings-tabs" role="tablist" aria-label="Configurações atuais do DeadlineEngine">
          {MAIN_CONFIG_CATEGORIES.map((category) => (
            <button key={category.id} type="button" className={`rules-tab ${activeTabId === category.id ? 'active' : ''}`} onClick={() => selectTab(category.id)} role="tab" aria-selected={activeTabId === category.id}>
              <span>{category.title}</span>
              <small>{category.kind === 'learning' ? 'Curadoria' : (Array.isArray(configurations[category.id]) ? configurations[category.id].length : 0)}</small>
            </button>
          ))}
        </div>
      ) : null}

      <section className="rules-card deadline-settings-card">
        <div className="rules-card-header">
          <div>
            <h3>{isCalendarManagement ? CALENDAR_MANAGEMENT_CATEGORY.title : activeCategory.title}</h3>
            <p>{isCalendarManagement ? CALENDAR_MANAGEMENT_CATEGORY.description : activeCategory.description}</p>
          </div>
          {!isSpecialCategory ? <div className="rules-card-actions"><button type="button" className="btn btn-primary" onClick={() => { setEditingRow(null); setModalOpen(true) }}><i className="fas fa-plus" /> Nova configuração</button></div> : null}
        </div>

        <div className="rules-card-body">
          {isCalendarManagement ? (
            <>
              <div className="deadline-calendar-submenu" role="tablist" aria-label="Gestão do calendário jurídico">
                {CALENDAR_SUBCATEGORIES.map((category) => (
                  <button key={category.id} type="button" className={calendarSection === category.id ? 'active' : ''} onClick={() => selectCalendarSection(category.id)} role="tab" aria-selected={calendarSection === category.id}>
                    <span>{category.id === 'legal_non_business_days' ? 'Dias não úteis jurídicos' : 'Calendários judiciais'}</span>
                    <small>{category.id === 'legal_non_business_days' ? 'Dias importados e manuais' : 'Fontes e sincronizações'}</small>
                  </button>
                ))}
              </div>
              <div className="deadline-calendar-section-heading">
                <h4>{configurationCategory.title}</h4>
                <p>{configurationCategory.description}</p>
              </div>
            </>
          ) : null}
          {!loading && isPhrasePatternsCategory ? (
            <div className="deadline-settings-list-controls">
              <label className="rules-field deadline-settings-source-filter"><span className="rules-field-label">Origem</span><select value={phrasePatternSource} onChange={(event) => { setPhrasePatternSource(event.target.value); setPhrasePatternPage(1) }}><option value="ALL">Todas as origens</option><option value="HUMAN_FEEDBACK">Human feedback</option><option value="HISTORICAL">Histórico</option><option value="SEED">Seed</option></select></label>
              <label className="rules-field deadline-settings-source-filter"><span className="rules-field-label">Aprendizado</span><select value={phrasePatternLearningState} onChange={(event) => { setPhrasePatternLearningState(event.target.value); setPhrasePatternPage(1) }}><option value="ALL">Todos os aprendizados</option><option value="SUPERVISED">Supervisionado</option><option value="GRADUATED">Promovido</option><option value="REJECTED">Rejeitado</option></select></label>
              <span>{filteredRows.length} registro(s) encontrado(s)</span>
            </div>
          ) : null}
          {!loading && isNonBusinessDaysCategory ? (
            <div className="deadline-settings-list-controls deadline-settings-calendar-filters">
              <div className="deadline-settings-filter-fields">
                <label className="rules-field deadline-settings-source-filter"><span className="rules-field-label">Tribunal</span><select value={nonBusinessDayCourt} onChange={(event) => setNonBusinessDayCourt(event.target.value)}><option value="ALL">Todos os tribunais</option><option value="NO_COURT">Sem tribunal (nacional/estadual)</option>{nonBusinessDayFilterOptions.courts.map((court) => <option key={court} value={court}>{court}</option>)}</select></label>
                <label className="rules-field deadline-settings-compact-filter"><span className="rules-field-label">Ano</span><select value={nonBusinessDayYear} onChange={(event) => setNonBusinessDayYear(event.target.value)}><option value="ALL">Todos os anos</option>{nonBusinessDayFilterOptions.years.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
                <label className="rules-field deadline-settings-source-filter"><span className="rules-field-label">Origem</span><select value={nonBusinessDayOrigin} onChange={(event) => setNonBusinessDayOrigin(event.target.value)}><option value="ALL">Todas as origens</option>{nonBusinessDayFilterOptions.origins.map((origin) => <option key={origin} value={origin}>{origin}</option>)}</select></label>
                <label className="rules-field deadline-settings-compact-filter"><span className="rules-field-label">Situação</span><select value={nonBusinessDayStatus} onChange={(event) => setNonBusinessDayStatus(event.target.value)}><option value="ALL">Todas</option><option value="ACTIVE">Ativos</option><option value="INACTIVE">Inativos</option></select></label>
                <label className="rules-field deadline-settings-search-filter"><span className="rules-field-label">Buscar</span><input type="search" value={nonBusinessDaySearch} onChange={(event) => setNonBusinessDaySearch(event.target.value)} placeholder="Descrição, UF, cidade ou escopo" /></label>
              </div>
              <div className="deadline-settings-filter-summary">
                <span>{filteredRows.length} de {activeRows.length} dia(s)</span>
                <button type="button" className="btn btn-sm btn-outline" onClick={() => { setNonBusinessDayCourt('ALL'); setNonBusinessDayYear('ALL'); setNonBusinessDayOrigin('ALL'); setNonBusinessDayStatus('ALL'); setNonBusinessDaySearch('') }} disabled={nonBusinessDayCourt === 'ALL' && nonBusinessDayYear === 'ALL' && nonBusinessDayOrigin === 'ALL' && nonBusinessDayStatus === 'ALL' && !nonBusinessDaySearch}>Limpar filtros</button>
              </div>
            </div>
          ) : null}

          {loading && !isSpecialCategory ? <div className="deadline-settings-state">Carregando configurações atuais do motor...</div> : null}

          {!loading && !isSpecialCategory ? (
            filteredRows.length ? (
              <>
                <div className="deadline-settings-table-wrapper">
                   <table className={`users-table deadline-settings-table deadline-settings-table-${configurationCategory.id}`}>
                     <thead><tr>{configurationCategory.columns.map((column) => <th key={column} className={`deadline-settings-column-${column}`}>{configurationCategory.columnLabels?.[column] || column}</th>)}<th>Ações</th></tr></thead>
                     <tbody>
                       {displayedRows.map((row) => (
                         <tr key={row.id}>
                          {configurationCategory.columns.map((column) => <td key={column} className={`deadline-settings-column-${column}`}>{isNonBusinessDaysCategory && column === 'description' ? <span className="deadline-settings-cell-clamp" title={formatValue(row[column])}>{formatColumnValue(column, row[column])}</span> : formatColumnValue(column, row[column])}</td>)}
                          <td><div className="deadline-settings-row-actions">
                            {isPhrasePatternsCategory && row.source !== 'SEED' && row.learning_state !== 'GRADUATED' ? <button type="button" className="btn btn-sm btn-primary" onClick={() => changeLearningState(row, 'GRADUATED')} disabled={saving || preparingPromotion}><i className={`fas ${preparingPromotion ? 'fa-spinner fa-spin' : 'fa-check'}`} /> Promover</button> : null}
                            {isPhrasePatternsCategory && row.source !== 'SEED' && row.learning_state === 'GRADUATED' ? <button type="button" className="btn btn-sm btn-outline" onClick={() => changeLearningState(row, 'SUPERVISED')} disabled={saving}><i className="fas fa-rotate-left" /> Supervisionar</button> : null}
                            {isPhrasePatternsCategory && row.source !== 'SEED' && row.learning_state !== 'REJECTED' ? <button type="button" className="btn btn-sm btn-danger" onClick={() => changeLearningState(row, 'REJECTED')} disabled={saving}><i className="fas fa-ban" /> Rejeitar</button> : null}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              onClick={() => { setEditingRow(row); setModalOpen(true) }}
                              disabled={isReadOnlyNonBusinessDay(configurationCategory, row)}
                              title={isReadOnlyNonBusinessDay(configurationCategory, row) ? 'Dias importados e seeds são gerenciados pela origem e não podem ser editados aqui.' : undefined}
                            ><i className="fas fa-pen" /> Editar</button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteConfiguration(row)}
                              disabled={isReadOnlyNonBusinessDay(configurationCategory, row)}
                              title={isReadOnlyNonBusinessDay(configurationCategory, row) ? 'Dias importados e seeds são gerenciados pela origem e não podem ser desativados aqui.' : undefined}
                            ><i className="fas fa-ban" /> Desativar</button>
                          </div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {isPhrasePatternsCategory ? <footer className="deadline-settings-pagination"><span>Exibindo <strong>{displayedRows.length}</strong> de <strong>{filteredRows.length}</strong> registros</span><div><select value={phrasePatternPageSize} onChange={(event) => { setPhrasePatternPageSize(Number(event.target.value)); setPhrasePatternPage(1) }}>{[10, 20, 50].map((size) => <option key={size} value={size}>{size} / página</option>)}</select><button type="button" className="btn btn-sm btn-outline" disabled={phrasePatternPage <= 1} onClick={() => setPhrasePatternPage((page) => page - 1)}>Anterior</button><span>Página {phrasePatternPage} de {totalPages}</span><button type="button" className="btn btn-sm btn-outline" disabled={phrasePatternPage >= totalPages} onClick={() => setPhrasePatternPage((page) => page + 1)}>Próxima</button></div></footer> : null}
              </>
            ) : <div className="deadline-settings-state">Nenhuma configuração encontrada nesta categoria.</div>
          ) : null}

          {isCalendarCategory ? <DeadlineEngineCalendarPanel /> : null}
          {!loading && isLearningCategory ? <DeadlineEngineLearningPanel /> : null}
        </div>
      </section>

       {!isSpecialCategory ? <ConfigurationModal open={modalOpen} category={configurationCategory} initialValue={editingRow} saving={saving} onClose={() => setModalOpen(false)} onSave={saveConfiguration} /> : null}
      <PromotionModal promotion={promotion} saving={saving} onClose={() => setPromotion(null)} onConfirm={confirmPromotion} areaOptions={contextOptions.areas} documentNatureOptions={contextOptions.documentNatures} />
    </div>
  )
}
